import { HttpClient, provideHttpClient, withInterceptorsFromDi, withXhr } from "@angular/common/http";
import { enableProdMode, EnvironmentProviders, ErrorHandler, inject, Provider, provideAppInitializer, provideZoneChangeDetection } from "@angular/core";
import { bootstrapApplication } from "@angular/platform-browser";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { provideRouter, withComponentInputBinding, withEnabledBlockingInitialNavigation } from "@angular/router";
import { ContentViewerComponent } from "@app/components/content-viewer/content-viewer.component";
import { noAuthProviders } from "@app/msal/no-auth.providers";
import { CatalogMobileDetailsComponent } from "@app/public/catalog/catalog-mobile-details/catalog-mobile-details.component";
import { CatalogMobileInfoComponent } from "@app/public/catalog/catalog-mobile-info/catalog-mobile-info.component";
import { CatalogMobileComponent } from "@app/public/catalog/catalog-mobile/catalog-mobile.component";
import { CatalogComponent } from "@app/public/catalog/catalog.component";
import { CatalogInfoViewerComponent } from "@app/viewers/cataloginfoviewer/cataloginfoviewer.component";
import { HtmlviewerComponent } from "@app/viewers/htmlviewer/htmlviewer.component";
import { OverviewViewerComponent } from "@app/viewers/overview-viewer/overview-viewer.component";
import { provideTranslateService, TranslateCompiler, TranslateLoader } from "@ngx-translate/core";
import { TranslateMessageFormatCompiler } from "ngx-translate-messageformat-compiler";
import { isAuthenticationNeededAtStartup, isAuthResponseBridgeTarget, removeLogoutStateFromUrl } from "@commonHelpers/auth-needed";
import { ChunkRecoveryService } from "@commonServices/chunk-recovery.service";
import { MsalB2CService } from "@commonServices/msalb2c.service";
import { environment } from "@environments/environment";
import { globalEnvironment } from "@environments/globalEnvironment";
import { Client as AccountPublicApi } from "@interfaces/HttpClient/AccountApiPublicModels";
import { Client as AnalyticsTrackingApi } from "@interfaces/HttpClient/AnalyticsApiTrackingModels";
import { Client as CatalogPublicAPI } from "@interfaces/HttpClient/CatalogApiPublicModels";
import { ApplicationInsightsService } from "@services/application-insights.service";
import { CacheService } from "@services/cache.service";
import { CssVariableService } from "@services/css-variable.service";
import { ErrorHandlerService } from "@services/error-handler.service";
import { GlobalVarService } from "@services/global-var.service";
import { IntervalService } from "@services/interval.service";
import { RedirectService } from "@services/redirect.service";
import { SoftwareIntegrationCallbackService } from "@services/software-integration-callback.service";
import { SoftwarepartnerApiService } from "@services/softwarepartner-api.service";
import { TrackEventService } from "@services/track-event.service";
import { TranslationService } from "@services/translation.service";
import { UserAuthorizationService } from "@services/user-authorization.service";
import { ViewTypeService } from "@services/view-type.service";
import { WatchlistActionService } from "@services/watchlist-action.service";
import { WatchlistService } from "@services/watchlist.service";
import { TranslateLoaderService } from "@services/translate-loader.service";
import { AppComponent } from "./app/app.component";
import { routes } from "./app/routes";

export const getBaseUrl = () => document.getElementsByTagName("base")[0].href

const providers = [{ provide: "BASE_URL", useFactory: getBaseUrl, deps: [] }];

if (environment.production) {
	enableProdMode();
}

// SEO-relevante Komponenten vorladen
//
// Die Liste ist an einer Stelle geteilt, und die Teilung ist inhaltlich: die Auth-Provider
// müssen genau hier eingehängt werden, weil `provideAppInitializer` in Providerreihenfolge
// ausgeführt wird. Ihr Initializer erledigt `handleRedirectPromise`, `initialize()` und den
// Wiederanlauf einer hinterlegten Anmeldung - alles, was danach kommt, setzt initialisiertes
// MSAL voraus: `startSession()`, `checkForUpdates()` und `initTranslation()` rufen die Api, wo
// der MSAL-Interceptor ein Token anhängen will, und `subscribeState()` abonniert die
// Auth-Ereignisse. Hängt man die Auth-Provider stattdessen hinten an, laufen diese Initializer
// vor der Initialisierung und der Anmeldewiederanlauf erst nach allen ihren Awaits - sichtbar
// als "die Anmeldung wird nicht ausgelöst", und je nach Seite unterschiedlich, weil die
// vorgezogenen Initializer pro Route andere Aufrufe machen.
const commonProvidersBeforeAuth: (Provider | EnvironmentProviders)[] = [
	providers,
	// Ab Angular 21 muss zone.js-basierte Change Detection explizit bereitgestellt
	// werden; bis Angular 20 war der Scheduler implizit dabei.
	provideZoneChangeDetection(),
	CatalogComponent,
	CatalogMobileComponent,
	CatalogMobileDetailsComponent,
	CatalogMobileInfoComponent,
	CatalogInfoViewerComponent,
	HtmlviewerComponent,
	ContentViewerComponent,
	OverviewViewerComponent,
	// Kendo braucht Animationen leider noch (Peer auf @angular/animations).
	// provideAnimationsAsync statt provideAnimations: der Animations-Motor wird erst beim
	// ersten Bedarf nachgeladen statt fest ins initiale Bundle zu wandern. Für die
	// SEO-Seiten zahlt sich das aus - `src/app/public/**` enthält keinen einzigen
	// Kendo-Import, dort wird also nie animiert.
	// TODO: ganz entfernen, wenn sich bei Kendo dort mal jemand angesprochen fühlt seine Arbeit zu machen
	provideAnimationsAsync(),
	// Muss vor provideRouter stehen, damit ein eventueller Cache-Buster aus der URL
	// verschwunden ist, bevor der Router die erste Navigation ausführt.
	provideAppInitializer(() => {
		const initializerFn = ((chunkRecoveryService: ChunkRecoveryService) => () => chunkRecoveryService.resetAfterSuccessfulBoot(globalEnvironment.buildDate?.toString()))(inject(ChunkRecoveryService));
		return initializerFn();
	}),
	provideRouter(routes, withEnabledBlockingInitialNavigation(), withComponentInputBinding()),
	WatchlistActionService,
	ViewTypeService,
	WatchlistService,
	TrackEventService,
	// useExisting, nicht useClass: ErrorHandlerService ist `providedIn: 'root'`, und useClass
	// hätte daneben eine zweite Instanz erzeugt. Angular benutzte dann die eine für
	// unbehandelte Fehler, während AppComponent die andere ansprach - insertDefaultList() und
	// setInitialized() liefen also auf einem Objekt, das der Fehlerweg nie zu sehen bekam.
	{ provide: ErrorHandler, useExisting: ErrorHandlerService },
	CssVariableService,
	UserAuthorizationService,
	GlobalVarService,
	CacheService,
	IntervalService,
	ApplicationInsightsService,
	MsalB2CService,
	provideAppInitializer(() => {
		const initializerFn = ((redirectService: RedirectService) => () => redirectService.possibleRedirectToNewTarget())(inject(RedirectService));
		return initializerFn();
	}),
];

// Ab hier gilt: MSAL ist initialisiert. Siehe Kommentar oben.
const commonProvidersAfterAuth: (Provider | EnvironmentProviders)[] = [
	// withXhr ist Pflicht, nicht Geschmackssache: ab Angular 22 nutzt HttpClient
	// standardmäßig fetch, und FetchBackend liefert keine Upload-Progress-Events.
	// azure-blob.service setzt reportProgress und liest HttpEventType.UploadProgress
	// für die Fortschrittsanzeige beim Katalog-Upload — mit fetch wäre sie
	// stillschweigend bei 0 Prozent stehen geblieben.
	provideHttpClient(withXhr()),
	provideTranslateService({
		lang: 'de',
		fallbackLang: 'de',
		loader: {
			provide: TranslateLoader,
			useFactory: (http: HttpClient) => new TranslateLoaderService(http),
			deps: [HttpClient]
		},
		compiler: {
			provide: TranslateCompiler,
			useClass: TranslateMessageFormatCompiler
		}
	}),
	provideAppInitializer(() => {
		const initializerFn = ((intervalService: IntervalService) => () => intervalService.init())(inject(IntervalService));
		return initializerFn();
	}),
	provideAppInitializer(() => {
		const initializerFn = ((softwarepartnerApiService: SoftwarepartnerApiService) => () => { softwarepartnerApiService.init() })(inject(SoftwarepartnerApiService));
		return initializerFn();
	}),
	provideAppInitializer(() => {
		const initializerFn = ((viewTypeService: ViewTypeService) => () => viewTypeService.initializeViewTypeByWidthSubscription())(inject(ViewTypeService));
		return initializerFn();
	}),

	{
		provide: AccountPublicApi,
		useFactory: (http: HttpClient) => new AccountPublicApi(http, environment.accountApi),
		deps: [HttpClient]
	},
	{
		provide: AnalyticsTrackingApi,
		useFactory: (http: HttpClient) => new AnalyticsTrackingApi(http, environment.analyticsApi),
		deps: [HttpClient]
	},
	provideAppInitializer(() => {
		const initializerFn = ((softwareIntegrationCallbackService: SoftwareIntegrationCallbackService) => async () => { await softwareIntegrationCallbackService.startSession(); })(inject(SoftwareIntegrationCallbackService));
		return initializerFn();
	}),
	// AnalyticsTrackingService has to be initialized after SoftwareIntegrationCallbackService, to make shure that softwareIntegrationSession already exists, if applicable
	provideAppInitializer(() => {
		const initializerFn = ((cacheService: CacheService) => async () => { await cacheService.checkForUpdates(); })(inject(CacheService));
		return initializerFn();
	}),
	provideAppInitializer(() => {
		const initializerFn = ((translationService: TranslationService) => async () => { await translationService.initTranslation(); })(inject(TranslationService));
		return initializerFn();
	}),
	provideAppInitializer(() => {
		const initializerFn = ((userAutorisationService: UserAuthorizationService) => () => userAutorisationService.subscribeState())(inject(UserAuthorizationService));
		return initializerFn();
	}),
	{
		provide: CatalogPublicAPI,
		useFactory: (http: HttpClient) => new CatalogPublicAPI(http, environment.catalogApi),
		deps: [HttpClient]
	},
	// withXhr auch hier: dieser zweite provideHttpClient-Aufruf ist Altbestand, und
	// ohne die Option würde er den Backend-Provider wieder auf fetch zurückstellen.
	provideHttpClient(withInterceptorsFromDi(), withXhr()),
];

// MSAL nur laden, wenn es beim Start gebraucht wird.
//
// @azure/msal-* wog 323 kB roh in 132 Modulen im initialen Bundle - für jeden anonymen Besucher
// und jeden Crawler, obwohl beide sich nie anmelden. Der Grund war die Verdrahtung direkt hier:
// MSAL_INSTANCE, MsalService, MsalGuard und MsalBroadcastService als statische Provider.
//
// Jetzt entscheidet isAuthenticationNeededAtStartup(), welcher Providersatz zum Zug kommt.
// Gebraucht wird MSAL bei Kontoeinträgen im localStorage (angemeldeter Nutzer), bei einer
// Antwort des Anmeldedienstes in der Adresse oder nach einem Anmeldeklick. Sonst kommt eine
// No-Auth-Fassung, die "nicht angemeldet" meldet und beim Anmelden auf den MSAL-Zweig umschaltet.
//
// Dynamisch geladen wird deshalb nur noch der MSAL-Zweig - er allein enthält MSAL-Laufzeitcode.
// noAuthProviders wird statisch importiert: no-auth.adapter und msalb2c.interceptor kennen MSAL
// ausschließlich über `import type`, der Compiler entfernt diese Importe also restlos. Als
// dynamischer Import kostete dieser Zweig den anonymen Pfad dagegen zwei blockierende Netzrunden
// vor dem Bootstrap - also genau auf dem wichtigsten SEO-Weg, auf dem MSAL nie gebraucht wird.
//
// Angemeldete Nutzer laden MSAL weiterhin sofort, und das ist notwendig: seit msal-browser 4 ist
// der localStorage-Cache mit einem Session-Cookie-Schlüssel verschlüsselt - Name, E-Mail und
// Initialen im Konto-Menü sind ohne MSAL nicht lesbar.
//

// Der Start bleibt asynchron: der MSAL-Zweig wird per dynamischem Import geholt, und die
// Antwortweitergabe im versteckten iframe wartet ebenfalls. Top-Level-await geht hier nicht:
// esbuild lehnt es für die aus der browserslist abgeleitete Zielumgebung ab.
const startApplication = async (): Promise<void> => {
	// Antwort im versteckten MSAL-iframe oder im Popup: nur ans Hauptfenster weiterreichen, die
	// Anwendung hier nicht starten. Begründung in isAuthResponseBridgeTarget().
	if (isAuthResponseBridgeTarget()) {
		const { broadcastResponseToMainFrame } = await import("@azure/msal-browser/redirect-bridge");
		await broadcastResponseToMainFrame();
		return;
	}

	const authenticationNeeded = isAuthenticationNeededAtStartup();
	// Erst entscheiden, dann bereinigen: die Erkennung braucht das `state` des Abmelde-Rücksprungs,
	// damit MSAL geladen wird und sein SIGNOUT-Flag abräumt - Angular soll den Parameter aber nicht
	// mehr sehen, deshalb vor dem Bootstrap aus der Adresse nehmen.
	removeLogoutStateFromUrl();

	const authProviders = authenticationNeeded
		? (await import("@app/msal/msal-auth.providers")).msalAuthProviders()
		: noAuthProviders();

	await bootstrapApplication(AppComponent, {
		providers: [...commonProvidersBeforeAuth, ...authProviders, ...commonProvidersAfterAuth]
	});
};

void startApplication().catch(err => console.log(err));